/**
 * NOT CURRENTLY USED.
 *
 * The teacher portal navigation is attendance-only (see NAV_ITEMS in StaffLayout), so
 * this page is unreachable from the UI. Its route is still registered in StaffApp, so
 * direct links resolve and re-enabling it only takes restoring its nav entry.
 *
 * It has not been carried through the mobile-first attendance rework: it is desktop-
 * oriented and predates the school/grade roster segmentation. Left as-is on purpose —
 * do not treat it as a current example of how these screens should look.
 */
import { useQuery } from '@tanstack/react-query';
import { LocationPill } from '../../components/LocationPill';
import { PROGRAM_LOCATION_EMBED, programLocationName, type WithProgramLocation } from '../../types/location';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { supabase } from '../supabase';
import { StaffLayout } from '../StaffLayout';
import { formatDateDisplay } from '../../utils/date';
import { getScheduleDescription } from '../../types/program';
import { getUpcomingBirthdays, BirthdayInput, BirthdayStudent } from '../../utils/birthdays';
import { BirthdayBanner } from '../../components/BirthdayBanner';
import { NON_TEACHING_TYPES_FILTER } from '../programSessions';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
}

interface TeachingProgram extends WithProgramLocation {
  id: string;
  name: string;
  type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
}

interface Props {
  staffUser: StaffUser;
}

function StatCard({ label, value, loading, onClick }: { label: string; value: number; loading: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-blue-300 hover:shadow-sm transition-all group w-full"
    >
      <p className="text-sm text-gray-500 group-hover:text-blue-600 transition-colors">{label}</p>
      {loading ? (
        <div className="h-8 w-16 bg-gray-100 animate-pulse rounded mt-1" />
      ) : (
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      )}
    </button>
  );
}

export function StaffOverview({ staffUser }: Props) {
  const navigate = useNavigate();
  const scopedIds = staffUser.program_ids;

  const { data: programs = [], isLoading: programsLoading } = useQuery<TeachingProgram[]>({
    queryKey: ['staff-overview-programs', scopedIds],
    queryFn: async () => {
      let q = supabase
        .from('programs')
        .select(`id, name, type, schedule_cron, start_date, end_date, ${PROGRAM_LOCATION_EMBED}`)
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (scopedIds) q = q.in('id', scopedIds);
      const { data, error } = await q.order('start_date');
      if (error) throw error;
      return (data ?? []) as TeachingProgram[];
    },
  });

  const programIds = programs.map(p => p.id);

  // Per-program enrolled counts, unique student total, and the unique student-id set —
  // all via staff_camp_roster (same RPC as Attendance/Schedule). Plain enrollment
  // queries miss Full Summer Pass students, who are enrolled in the pass program
  // rather than each individual camp.
  const { data: rosterStats = { counts: {} as Record<string, number>, totalStudents: 0, totalEnrollments: 0, studentIds: [] as string[] }, isLoading: rosterLoading } = useQuery<{
    counts: Record<string, number>;
    totalStudents: number;
    totalEnrollments: number;
    studentIds: string[];
  }>({
    queryKey: ['staff-overview-roster-stats', programIds],
    enabled: programIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        programIds.map(id =>
          supabase.rpc('staff_camp_roster', { p_program_id: id }).then(r => {
            if (r.error) throw r.error;
            return { id, rows: (r.data ?? []) as { student_id: string }[] };
          }),
        ),
      );
      const counts: Record<string, number> = {};
      const studentIds = new Set<string>();
      let totalEnrollments = 0;
      for (const { id, rows } of results) {
        counts[id] = rows.length;
        totalEnrollments += rows.length;
        for (const row of rows) studentIds.add(row.student_id);
      }
      return { counts, totalStudents: studentIds.size, totalEnrollments, studentIds: Array.from(studentIds) };
    },
  });

  const enrollmentCounts = rosterStats.counts;
  const totalStudents = rosterStats.totalStudents;
  const totalEnrollments = rosterStats.totalEnrollments;
  const isLoading = programsLoading || rosterLoading;

  // Upcoming birthdays for students in this teacher's programs, so the class can
  // prepare. Student set comes from the roster RPC (includes Full Summer Pass
  // students); we then fetch their birthdates and reuse the shared window logic.
  const rosterStudentIds = rosterStats.studentIds;
  const { data: birthdayStudents = [] } = useQuery<BirthdayStudent[]>({
    queryKey: ['staff-overview-birthdays', rosterStudentIds],
    enabled: rosterStudentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('students')
        .select('id, first_name, last_name, date_of_birth')
        .in('id', rosterStudentIds);
      if (error) throw error;
      return getUpcomingBirthdays((data ?? []) as BirthdayInput[]);
    },
  });

  return (
    <StaffLayout staffUser={staffUser}>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Overview</h1>
      <p className="text-sm text-gray-500 mb-6">Your programs and students</p>

      {birthdayStudents.length > 0 && (
        <div className="mb-6">
          <BirthdayBanner
            students={birthdayStudents}
            onClick={() => navigate('/staff-app/students')}
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="Programs" value={programs.length} loading={isLoading} onClick={() => navigate('/staff-app/schedule')} />
        <StatCard label="Students" value={totalStudents} loading={isLoading} onClick={() => navigate('/staff-app/students')} />
        <StatCard label="Enrollments" value={totalEnrollments} loading={isLoading} onClick={() => navigate('/staff-app/attendance')} />
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-3">Your Programs</h2>
      {programsLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : programs.length === 0 ? (
        <p className="text-gray-500 text-sm">No programs assigned yet.</p>
      ) : (
        <div className="space-y-3">
          {programs.map(p => (
            <button
              key={p.id}
              onClick={() => navigate(`/staff-app/attendance/${p.id}`)}
              className="w-full bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-4 text-left hover:border-blue-300 hover:shadow-sm transition-all group"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">{p.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{getScheduleDescription(p.schedule_cron)}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatDateDisplay(p.start_date)} – {formatDateDisplay(p.end_date)}
                </p>
                <LocationPill name={programLocationName(p)} className="mt-1.5" />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-900">{enrollmentCounts[p.id] ?? 0}</span>
                  <p className="text-xs text-gray-500">enrolled</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
              </div>
            </button>
          ))}
        </div>
      )}
    </StaffLayout>
  );
}
